import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Upload } from 'lucide-react';
import { categoryList } from '../../data/categoryData';
import { useEmailSubmit } from '../../hooks/useEmailSubmit';

const fieldClass = "w-full px-4 py-3 bg-white border border-[#E5E0D8] text-sm text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#1E3D66] focus:ring-1 focus:ring-[#1E3D66]/20 outline-none transition-all disabled:bg-[#fafaf9] disabled:cursor-not-allowed";
const labelClass = "block text-xs font-semibold tracking-wider uppercase text-[#0B2A57] mb-2";

const SupplierRegistrationForm = () => {
  const formRef = useRef();
  const [catalogue, setCatalogue] = useState(null);
  const { isSubmitting, submit } = useEmailSubmit({
    successMessage: 'Registration submitted successfully! We will review and contact you.',
    errorMessage: 'Failed to submit. Please try again or email info@taigasourcing.com',
    sheetName: 'Supplier Registrations',
  });

  const buildParams = (fd, form) => {
    const exportExp = fd.get('export_experience')
      ? (form.export_experience.selectedOptions[0]?.text || fd.get('export_experience'))
      : '-';
    return {
      emailParams: {
        name: fd.get('user_name'),
        email: fd.get('user_email'),
        phone: fd.get('phone'),
        message: `Request Type: Supplier Registration
Contact Person: ${fd.get('contact_person')}
City: ${fd.get('city')}
Product Category: ${fd.get('product_category')}
MOQ: ${fd.get('moq') || '-'}
Monthly Capacity: ${fd.get('monthly_capacity') || '-'}
Export Experience: ${exportExp}
Certifications: ${fd.get('certifications') || '-'}
Website: ${fd.get('website') || '-'}

Manufacturing Capability:
${fd.get('message')}`,
      },
      sheetRow: {
        'Company Name': fd.get('user_name'),
        'Contact Person': fd.get('contact_person'),
        Phone: fd.get('phone'),
        Email: fd.get('user_email'),
        City: fd.get('city'),
        'Product Category': fd.get('product_category'),
        'Manufacturing Capability': fd.get('message'),
        MOQ: fd.get('moq') || '-',
        'Monthly Capacity': fd.get('monthly_capacity') || '-',
        'Export Experience': exportExp,
        Certifications: fd.get('certifications') || '-',
        Website: fd.get('website') || '-',
      },
    };
  };

  const handleSubmit = (e) => submit(e, formRef, buildParams, () => setCatalogue(null));

  return (
    <div className="bg-[#fafaf9] p-8">
      <h3 className="text-lg font-bold text-[#0B2A57] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>
        Supplier Registration
      </h3>
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-5" data-testid="supplier-form">
        <div>
          <label className={labelClass}>Company Name *</label>
          <input type="text" name="user_name" required disabled={isSubmitting} className={fieldClass} placeholder="Company name" data-testid="supplier-company" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Contact Person *</label>
            <input type="text" name="contact_person" required disabled={isSubmitting} className={fieldClass} placeholder="Full name" data-testid="supplier-contact" />
          </div>
          <div>
            <label className={labelClass}>Phone *</label>
            <input type="tel" name="phone" required disabled={isSubmitting} className={fieldClass} placeholder="+91..." data-testid="supplier-phone" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Email *</label>
            <input type="email" name="user_email" required disabled={isSubmitting} className={fieldClass} placeholder="email@company.com" data-testid="supplier-email" />
          </div>
          <div>
            <label className={labelClass}>City *</label>
            <input type="text" name="city" required disabled={isSubmitting} className={fieldClass} placeholder="City" data-testid="supplier-city" />
          </div>
        </div>
        <div>
          <label className={labelClass}>Product Category *</label>
          <select name="product_category" required disabled={isSubmitting} className={fieldClass} data-testid="supplier-category">
            <option value="">Select Category</option>
            {categoryList.map((cat) => (
              <option key={cat.slug} value={cat.title}>{cat.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Manufacturing Capability *</label>
          <textarea name="message" required disabled={isSubmitting} rows={3} className={fieldClass + " resize-none"} placeholder="Describe your manufacturing capabilities..." data-testid="supplier-capability" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>MOQ</label>
            <input type="text" name="moq" disabled={isSubmitting} className={fieldClass} placeholder="e.g., 500 pcs" data-testid="supplier-moq" />
          </div>
          <div>
            <label className={labelClass}>Monthly Capacity</label>
            <input type="text" name="monthly_capacity" disabled={isSubmitting} className={fieldClass} placeholder="e.g., 10,000 pcs" data-testid="supplier-capacity" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Export Experience</label>
            <select name="export_experience" disabled={isSubmitting} className={fieldClass} data-testid="supplier-export">
              <option value="">Select</option>
              <option value="none">No export experience</option>
              <option value="limited">Limited export experience</option>
              <option value="regular">Regular exporter</option>
              <option value="extensive">Extensive export history</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Certifications</label>
            <input type="text" name="certifications" disabled={isSubmitting} className={fieldClass} placeholder="ISO, SEDEX, etc." data-testid="supplier-certs" />
          </div>
        </div>
        <div>
          <label className={labelClass}>Website</label>
          <input type="url" name="website" disabled={isSubmitting} className={fieldClass} placeholder="https://..." data-testid="supplier-website" />
        </div>
        <div>
          <label className={labelClass}>Catalogue Upload</label>
          <label className="flex items-center gap-3 px-4 py-3 bg-white border border-dashed border-[#E5E0D8] cursor-pointer hover:border-[#1E3D66] transition-colors">
            <Upload size={18} className="text-[#6B7280]" />
            <span className="text-sm text-[#6B7280]">{catalogue ? catalogue.name : 'Upload catalogue...'}</span>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => setCatalogue(e.target.files[0])} data-testid="supplier-catalogue" />
          </label>
        </div>
        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
          className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0B2A57] text-white text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="supplier-submit"
        >
          {isSubmitting ? 'Submitting...' : (
            <>Register as a Supplier <Send size={18} /></>
          )}
        </motion.button>
        <p className="text-xs text-center text-[#9CA3AF]">
          For catalogue files, please email info@taigasourcing.com
        </p>
      </form>
    </div>
  );
};

export default SupplierRegistrationForm;
